/**
 * Reading a `GET /get_summary` reply: is the result there, still coming, or not
 * coming at all.
 *
 * Lives apart from propertySummaryService because that module reaches axios,
 * which Jest cannot transform in this project — and this is the one part of the
 * property summary that is genuinely uncertain, so it is the part that needs
 * tests. What the backend returns *before* a summary exists has not been
 * observed: this workspace has no property session. So every plausible shape is
 * treated as "not ready yet", and only the markers this API family documents
 * for a rejection are treated as failure.
 */

/**
 * Keys distinctive enough to say "this object is a summary".
 *
 * Needed because the result's position in the envelope is unknown — it may be
 * the body, or under summary/result/data. Recognising the payload is more
 * robust than guessing the wrapper, and it cannot mistake a status envelope for
 * a result.
 */
const SUMMARY_KEYS = [
  "property_type",
  "overall_condition",
  "narrative",
  "visible_damages",
  "systems",
  "checklist",
  "image_inventory",
  "photo_coverage",
];

export const looksLikeSummary = (value) =>
  Boolean(value) &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  SUMMARY_KEYS.some((key) => key in value);

/** This API family returns documents as JSON strings in places. */
const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const CANDIDATE_KEYS = ["summary", "result", "image_summary", "data", "response"];

/**
 * Where the summary is, wherever it is.
 *
 * Checks the body itself, then the likely wrappers, then one level inside them,
 * accepting a JSON string at any step.
 */
export const findSummary = (body) => {
  const seen = new Set();

  const search = (value, depth) => {
    if (!value || depth > 2) return null;

    const object = typeof value === "string" ? parseMaybeJson(value) : value;
    if (!object || typeof object !== "object" || Array.isArray(object)) return null;
    if (seen.has(object)) return null;
    seen.add(object);

    if (looksLikeSummary(object)) return object;

    for (const key of CANDIDATE_KEYS) {
      if (key in object) {
        const found = search(object[key], depth + 1);
        if (found) return found;
      }
    }
    return null;
  };

  return search(body, 0);
};

/**
 * Whether the result is there, still coming, or will not come.
 *
 * Deliberately asymmetric: only the markers this API family documents for a
 * rejection count as failure — `status: -1` and a capital `Error` (see
 * assertV2Ok in apiGeneration.js). A bare `message` is treated as "still
 * running", because "summary not generated yet" is the likeliest thing for a
 * backend to say while it works, and reading that as failure would abandon a
 * job that was about to succeed.
 */
export const readSummaryResponse = (body) => {
  if (body === null || body === undefined || body === "") {
    return { state: "pending", reason: null, result: null };
  }

  const rejection =
    body?.status === -1 ||
    body?.status === "-1" ||
    (typeof body?.Error === "string" && body.Error.trim());

  if (rejection) {
    return {
      state: "failed",
      reason:
        (typeof body?.Error === "string" && body.Error.trim()) ||
        body?.message ||
        body?.Message ||
        "The backend rejected the request",
      result: null,
    };
  }

  const result = findSummary(body);
  if (result) return { state: "ready", reason: null, result };

  return {
    state: "pending",
    reason:
      (typeof body?.Message === "string" && body.Message.trim()) ||
      (typeof body?.message === "string" && body.message.trim()) ||
      null,
    result: null,
  };
};

/**
 * Whether an HTTP status from `get_summary` means "not generated yet".
 *
 * 404 does. The route exists — it answers 401 unauthenticated, like every other
 * route on that host — so a 404 with a valid token is the absence of a summary,
 * not the absence of an endpoint. Treating it as an error abandoned the poll on
 * the one status that means keep waiting.
 *
 * 204 and 202 are included as the other conventional ways to say "nothing yet"
 * / "accepted, still working", so a backend that switches to either does not
 * break this.
 */
export const isNotYetStatus = (status) =>
  status === 404 || status === 204 || status === 202;
